import React from 'react';
import { ArrowRight, Heart, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-gradient-to-br from-[#FFF7F1] via-[#FFEFE2] to-[#FFE4CF]"
    >
      {/* Decorative blobs */}
      <div className="absolute -top-20 -left-20 w-96 h-96 bg-[#F55B26]/10 rounded-full blur-3xl" />
      <div className="absolute top-40 -right-24 w-96 h-96 bg-[#FFB347]/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-[#0B3B44]/5 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
        <div className="space-y-7">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 backdrop-blur border border-[#F55B26]/20 rounded-full text-sm font-medium text-[#F55B26] shadow-sm">
            <Sparkles className="w-4 h-4" />
            South African NGO · Est. April 2020
          </div>

          <h1 className="text-5xl lg:text-7xl font-extrabold text-[#0B3B44] leading-[1.05] tracking-tight">
            Bringing <span className="relative inline-block">
              <span className="text-[#F55B26]">hope</span>
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="heroUnderline" x1="0" x2="1">
                    <stop offset="0%" stopColor="#EC407A" />
                    <stop offset="25%" stopColor="#F55B26" />
                    <stop offset="50%" stopColor="#FFB347" />
                    <stop offset="75%" stopColor="#7CB342" />
                    <stop offset="100%" stopColor="#2196F3" />
                  </linearGradient>
                </defs>
                <path d="M0 8 Q100 -2 200 6" stroke="url(#heroUnderline)" strokeWidth="4" fill="none" strokeLinecap="round" />
              </svg>
            </span> to the less fortunate.
          </h1>

          <p className="text-lg text-slate-700 leading-relaxed max-w-xl">
            Hello Foundation raises funds through programs and initiatives to improve lives.
            From feeding <strong>600 meals a day</strong> across Gauteng to launching youth mental
            wellness support in 2027 — join us in making the world a better place.
          </p>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              onClick={() => scrollTo('#donate')}
              className="bg-[#F55B26] hover:bg-[#E14D1F] text-white font-semibold px-7 py-6 rounded-full shadow-lg hover:shadow-xl transition-all group"
            >
              <Heart className="w-5 h-5 mr-2" fill="white" />
              Donate Now
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => scrollTo('#about')}
              variant="outline"
              className="border-2 border-[#0B3B44] text-[#0B3B44] hover:bg-[#0B3B44] hover:text-white font-semibold px-7 py-6 rounded-full transition-all"
            >
              Learn Our Story
            </Button>
          </div>

          <div className="flex items-center gap-8 pt-6">
            <div>
              <div className="text-3xl font-extrabold text-[#EC407A]">1M+</div>
              <div className="text-xs text-slate-600 uppercase tracking-wider">Plates served</div>
            </div>
            <div className="w-px h-12 bg-slate-300" />
            <div>
              <div className="text-3xl font-extrabold text-[#2196F3]">6</div>
              <div className="text-xs text-slate-600 uppercase tracking-wider">Communities</div>
            </div>
            <div className="w-px h-12 bg-slate-300" />
            <div>
              <div className="text-3xl font-extrabold text-[#8E24AA]">2027</div>
              <div className="text-xs text-slate-600 uppercase tracking-wider">Hello Support</div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white transform lg:rotate-2 hover:rotate-0 transition-transform duration-500">
            <img
              src="https://images.unsplash.com/photo-1509099836639-18ba1795216d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxBZnJpY2FuJTIwY2hpbGRyZW58ZW58MHx8fHwxNzgzMDU5NzA0fDA&ixlib=rb-4.1.0&q=85"
              alt="African children smiling"
              className="w-full h-[500px] lg:h-[560px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B3B44]/40 via-transparent to-transparent" />
          </div>

          {/* Floating cards */}
          <div className="absolute -left-4 top-10 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-slate-100 animate-[float_6s_ease-in-out_infinite]">
            <div className="w-11 h-11 rounded-xl bg-[#FFEFE2] flex items-center justify-center">
              <Heart className="w-6 h-6 text-[#F55B26]" fill="#F55B26" />
            </div>
            <div>
              <div className="text-xs text-slate-500">Today</div>
              <div className="font-bold text-[#0B3B44]">620 meals served</div>
            </div>
          </div>

          <div className="absolute -right-4 bottom-8 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 max-w-[220px] animate-[float_6s_ease-in-out_infinite_1s]">
            <div className="flex -space-x-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-[#F55B26] border-2 border-white" />
              <div className="w-8 h-8 rounded-full bg-[#0B3B44] border-2 border-white" />
              <div className="w-8 h-8 rounded-full bg-[#FFB347] border-2 border-white" />
              <div className="w-8 h-8 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center text-[10px] font-bold text-[#0B3B44]">+42</div>
            </div>
            <div className="text-xs font-semibold text-[#0B3B44]">Volunteers this week</div>
            <div className="text-xs text-slate-500">Join our community</div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </section>
  );
};

export default Hero;
